import { Component, Input, input, output, OnInit, ViewChild , DestroyRef, inject } from '@angular/core';
import { MatTableDataSource, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AddressRow } from './address-row';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import _, { add } from 'lodash';
import { AddressInputV2Component } from '../address-input-v2/address-input-v2.component';
import { Address, Building, Customer } from '@app/core/stingray-api/models';
import { Observable, concat, finalize, forkJoin } from 'rxjs';
import { EditBuildingPanelOptions } from '../edit-building-panel/edit-building-panel-options';
import { AddressRowFactory } from './address-row-factory';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { MatCheckbox } from '@angular/material/checkbox';


@Component({
    selector: 'unite-address-selection-list',
    templateUrl: './address-selection-list.component.html',
    styleUrls: ['./address-selection-list.component.scss'],
    imports: [MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCheckbox, MatCellDef, MatCell, TranslateModule, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatProgressSpinner]
})
export class AddressSelectionListComponent implements OnInit {
  @Input() isLoadingAddresses = false;
  @Input() showChipsList = false;

  addressAdded = output<{ address: AddressRow, selected: boolean }>();
  selectionChanged = output<{ address: AddressRow, selected: boolean }>();
  
  addressesDataSource: MatTableDataSource<AddressRow> = new MatTableDataSource<AddressRow>([]);
  addressesDisplayedColumns: string[];

  constructor(
    private addressRowFactory: AddressRowFactory) {
  }

  ngOnInit(): void {
    this.addressesDisplayedColumns = [
      'checked', 
      'name'
    ];

    if (this.showChipsList){
      this.addressesDisplayedColumns.push('chips')
    }
  }

  masterToggle(): void {
    const select = !this.isAllSelected();
    this.addressesDataSource.data.forEach(r => r.selected = select);
  }

  isAllSelected(): boolean {
    const numSelected = this.addressesDataSource.data.filter(x => x.selected === true).length;
    const numRows = this.addressesDataSource.data.length;
    return numSelected > 0 && numSelected === numRows;
  }

  addressRowClick(addressRow: AddressRow): void {
    addressRow.selected = !addressRow.selected;
    this.selectionChanged.emit({address: addressRow, selected: addressRow.selected});
  }

  unlinkCombination(addressRow: AddressRow, event: Event){
    event.stopPropagation();

    // Remove the original address first (otherwise new additional addresses will be matched against the existing rows)
    this.removeAddressRow(addressRow);
    
    // Add the additional addresses
    this.isLoadingAddresses = true;
    const addAddresses = addressRow.additionalAddresses.map(address => this.addAddress(addressRow.editBuildingModel.customerId, address, true, true, null, addressRow.editBuildingModel.mortgageIds, addressRow.options, true, true, addressRow.owned));

    concat(...addAddresses)
      .pipe(
        finalize(() => {
          this.isLoadingAddresses = false;
        })
      )
      .subscribe();
  }

  private removeAddressRow(addressRow: AddressRow): void {
    this.addressesDataSource.data.splice(this.addressesDataSource.data.indexOf(addressRow), 1);
    this.addressesDataSource.data = Array.from(this.addressesDataSource.data);
  }
  
  private getExistingAddress(address: Address): AddressRow {
    const addresses = this.addressesDataSource.data;
    return addresses.filter(a => this.isMatchingAddress(a.address, address) || a.additionalAddresses.filter(x => this.isMatchingAddress(x, address)).length > 0)[0];
  }
  
  private isMatchingAddress(a: Address, b: Address): boolean {
    return a.houseNumber === b.houseNumber &&
      a.postalCode === b.postalCode &&
      (a.houseNumberAddition ?? '') === (b.houseNumberAddition ?? '');
  }
    
  public getAddresses(): AddressRow[] {
    const addresses = this.addressesDataSource.data;
    return _.orderBy(addresses, x => !x.editBuildingModel.isProcessed);
  }
    
  public getSelectedAddresses(): AddressRow[] {
    const addresses = this.addressesDataSource.data.filter(x => x.selected);
    return _.orderBy(addresses, x => !x.editBuildingModel.isProcessed);
  }

  public clearAddresses(){
    this.addressesDataSource.data = [];
  }
  
  public addAddress(customerId: string, address: Address, selected: boolean, isUnlinkedCombination: boolean, existingBuilding: Building, mortgageIds: string[], options: Partial<EditBuildingPanelOptions>, includeValue: boolean, includeEnergy: boolean, owned?: boolean): Observable<boolean> {
    return new Observable(obs => {

      const addresses = this.addressesDataSource.data;
      let existingAddress = this.getExistingAddress(address);
      
      if (!existingAddress) {
        this.addressRowFactory.createAddressRow(customerId, address, selected, isUnlinkedCombination, existingBuilding, mortgageIds, options, includeValue, includeEnergy, owned)
          .subscribe((addressRow: AddressRow) => {
            // Re-check after subscribe is much later
            existingAddress = this.getExistingAddress(address);

            if (!existingAddress) {
              this.addressesDataSource.data.push(addressRow);
              this.addressesDataSource.data = _.orderBy(this.addressesDataSource.data, x => x.name);

              this.addressAdded.emit({address: addressRow, selected: selected});

              obs.next(true);
              obs.complete();
            } else {
                completeAndSelectExistingAddres();
              }
            });  
      } else {
        completeAndSelectExistingAddres();
      }

      function completeAndSelectExistingAddres(): void {
        if (selected) {
          existingAddress.selected = true;
        }
        else{
          console.warn('Address already exists', address, addresses);
        }
        obs.next(false);
        obs.complete();
      }
    });
  }
  
}
